export const FREE_TIER_SHIFT_LIMIT = 10;
export const FREE_TIER_PATIENT_LIMIT = 1;

export interface SubscriptionState {
  subscribed: boolean;
  patientSlots: number | null;
}

export interface SubscriptionData {
  isOwner: boolean;
  shiftsCount: number;
  patientsCount: number;
}

export interface SubscriptionResult {
  isRequired: boolean;
  shiftsLimit: number;
  patientsLimit: number;
  canCreatePatient: boolean;
}

/**
 * Derives subscription requirement status from raw data.
 * Pure function extracted from useSubscriptionRequired hook.
 */
export function deriveSubscriptionStatus(
  data: SubscriptionData,
  subscription: SubscriptionState,
  isLoggingOut: boolean
): SubscriptionResult {
  const { isOwner, shiftsCount, patientsCount } = data;

  const isRequired = !isLoggingOut && isOwner && shiftsCount >= FREE_TIER_SHIFT_LIMIT;

  const patientsLimit = subscription.subscribed && subscription.patientSlots
    ? subscription.patientSlots
    : FREE_TIER_PATIENT_LIMIT;

  const canCreatePatient = !isOwner || patientsCount < patientsLimit;

  return {
    isRequired,
    shiftsLimit: FREE_TIER_SHIFT_LIMIT,
    patientsLimit,
    canCreatePatient,
  };
}

/**
 * Minimum number of consecutive successful "not subscribed" answers required
 * before the app blocks access. One negative answer is never enough: cold
 * starts, token refreshes and Stripe races produce false negatives.
 */
export const NEGATIVE_CHECKS_TO_BLOCK = 2;

export interface SubscriptionBlockInput {
  isOwner: boolean;
  /** Free tier exhausted (shift limit reached). */
  isRequired: boolean;
  subscribed: boolean;
  /** ISO date of the end of the current paid period, if known. */
  subscriptionEnd: string | null;
  /** Consecutive successful checks that answered "not subscribed". */
  consecutiveNegativeChecks: number;
  /** Any relevant data still loading / revalidating (auth, usage, check in flight). */
  isResolving: boolean;
  now?: number;
}

/**
 * Single source of truth for the subscription paywall.
 *
 * Bias: never block when in doubt. Blocking requires ALL of:
 *  - the user is an owner whose free tier is exhausted;
 *  - nothing relevant is still loading;
 *  - the server said "not subscribed" at least NEGATIVE_CHECKS_TO_BLOCK times in a row;
 *  - there is no last-known paid period still running.
 */
export function shouldBlockForSubscription(input: SubscriptionBlockInput): boolean {
  const {
    isOwner,
    isRequired,
    subscribed,
    subscriptionEnd,
    consecutiveNegativeChecks,
    isResolving,
    now = Date.now(),
  } = input;

  if (!isOwner || !isRequired) return false;
  if (isResolving) return false;
  if (subscribed) return false;
  if (consecutiveNegativeChecks < NEGATIVE_CHECKS_TO_BLOCK) return false;

  const end = subscriptionEnd ? Date.parse(subscriptionEnd) : NaN;
  if (!Number.isNaN(end) && end > now) return false;

  return true;
}

